import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { Skill, SkillCategory } from '@/types'
import { getSkill, updateSkill } from '@/lib/api'
import { Card } from '@/components/ui/Card'
import { TextField, TextAreaField, SelectField, SaveBar } from '@/components/ui/FormFields'
import { PendingBackendNotice } from '@/components/ui/EmptyState'

export function SkillDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [skill, setSkill] = useState<Skill | null>(null)
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    getSkill(id).then(setSkill)
  }, [id])

  if (!skill) return <div style={{ color: 'var(--text-secondary)' }}>Cargando…</div>

  function update<K extends keyof Skill>(key: K, value: Skill[K]) {
    setSkill((prev) => (prev ? { ...prev, [key]: value } : prev))
  }

  async function handleSave() {
    if (!skill) return
    setSaving(true)
    setResult(null)
    try {
      await updateSkill(skill.id, skill)
      setResult('Guardado — solo en esta sesión (sin backend conectado)')
    } catch (e) {
      setResult(e instanceof Error ? e.message : 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <PendingBackendNotice endpoint={`PATCH /skills/${skill.id}`} />
      <button onClick={() => navigate('/skills')} style={backLinkStyle}>← Volver a Skills</button>
      <h1 style={{ fontSize: 20, fontWeight: 500, marginBottom: 16 }}>{skill.name}</h1>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card>
          <TextField label="ID" value={skill.id} onChange={() => {}} mono />
          <TextField label="Nombre" value={skill.name} onChange={(v) => update('name', v)} />
          <TextField label="Versión" value={skill.version} onChange={(v) => update('version', v)} mono />
          <SelectField
            label="Categoría"
            value={skill.skill_category ?? 'operacional'}
            onChange={(v) => update('skill_category', v as SkillCategory)}
            options={['conversacional', 'operacional', 'analítico', 'marca']}
          />
        </Card>
        <Card>
          <TextAreaField label="Descripción" value={skill.description ?? ''} onChange={(v) => update('description', v)} rows={3} />
          <TextAreaField
            label="Docs (markdown completo)"
            value={skill.docs ?? ''}
            onChange={(v) => update('docs', v)}
            rows={12}
            placeholder="## Cuándo usar este skill&#10;...&#10;&#10;## Proceso&#10;1. ..."
          />
        </Card>
      </div>

      <SaveBar onSave={handleSave} saving={saving} result={result} />
    </div>
  )
}

const backLinkStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--text-secondary)',
  fontSize: 13,
  cursor: 'pointer',
  padding: 0,
  marginBottom: 12,
  display: 'block',
}
